import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Checkbox, Descriptions, Form, Input, Modal, Radio, Select, Space, Spin, Table, Typography, message } from 'antd';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { formatMoney } from '@/utils/format';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import * as vatReturnService from '@/services/vatReturn.service';
import type { VatReturnRow, VatSettlementSetup } from '@/services/vatReturn.service';

const { Text } = Typography;

const money = (n: number) => formatMoney(n, { display: 'plain', locale: 'en-US' });
const listOf = (res: any): any[] => {
  const data = res?.data ?? res;
  return Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
};

interface Props {
  returnId: string | null;
  onClose: () => void;
  onPosted: (row: VatReturnRow) => void;
}

interface FormValues {
  fta_supplier_id?: string;
  account_mode: 'existing' | 'create';
  vat_payable_account_id?: string;
  new_account_code?: string;
  new_account_name?: string;
  department_id?: string;
  remember: boolean;
}

/** Confirms the VAT settlement: accounts cleared, VAT Payable account (pick or create) and the FTA bill. */
const PostVatReturnModal: React.FC<Props> = ({ returnId, onClose, onPosted }) => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const [form] = Form.useForm<FormValues>();
  const [setup, setSetup] = useState<VatSettlementSetup | null>(null);
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const mode = Form.useWatch('account_mode', form);
  const payableId = Form.useWatch('vat_payable_account_id', form);

  const { data: suppliers = [] } = useQuery({
    queryKey: ['procurement-suppliers-list'],
    queryFn: async () => listOf(await api.get('/procurement/suppliers')),
    enabled: !!returnId,
  });
  const { data: accounts = [] } = useQuery({
    queryKey: ['chart-of-accounts-all'],
    queryFn: async () => listOf(await api.get('/finance/chart-of-accounts')).filter((a: any) => !a.is_group),
    enabled: !!returnId,
  });

  useEffect(() => {
    if (!returnId) return;
    let cancelled = false;
    setSetup(null);
    setLoading(true);
    vatReturnService
      .getVatSettlementSetup(returnId)
      .then((s) => {
        if (cancelled) return;
        setSetup(s);
        form.setFieldsValue({
          fta_supplier_id: s.fta_supplier?.id,
          account_mode: s.vat_payable_account ? 'existing' : 'create',
          vat_payable_account_id: s.vat_payable_account?.id,
          new_account_code: s.suggested_account.account_code,
          new_account_name: s.suggested_account.account_name,
          department_id: s.department_id ?? undefined,
          remember: true,
        });
      })
      .catch((e: any) => {
        if (cancelled) return;
        message.error(e?.response?.data?.message || t('couldNotLoadSettlementDetails'));
        onClose();
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [returnId, form, onClose, t]);

  const clearingIds = useMemo(() => new Set((setup?.clearing ?? []).map((c) => c.account_id)), [setup]);
  const supplierOptions = useMemo(
    () => suppliers.map((s: any) => ({ value: s.id, label: s.supplier_code ? `${s.supplier_name} (${s.supplier_code})` : s.supplier_name })),
    [suppliers]
  );
  const accountOptions = useMemo(
    () =>
      accounts
        .filter((a: any) => a.account_type === 'Liability' && a.is_active !== false && !clearingIds.has(a.id))
        .map((a: any) => ({ value: a.id, label: `${a.account_code} - ${a.account_name}` })),
    [accounts, clearingIds]
  );

  const payable = setup?.direction === 'payable';
  const blocked = !!setup && (setup.stale || setup.unresolved.length > 0);
  const payableLabel =
    mode === 'create'
      ? `${form.getFieldValue('new_account_code') || t('newAccountPlaceholder')} - ${form.getFieldValue('new_account_name') || t('vatPayableAccount')}`
      : accountOptions.find((o: { value: string; label: string }) => o.value === payableId)?.label ?? t('vatPayableAccount');

  const journalRows = useMemo(() => {
    if (!setup) return [];
    const rows = setup.clearing.map((c) => {
      const signed = c.side === 'output' ? c.amount : -c.amount;
      return {
        key: `${c.side}:${c.account_id}`,
        account: `${c.account_code} - ${c.account_name}`,
        note:
          c.side === 'output'
            ? t('outputVatDocumentsNote', { count: c.documents })
            : t('inputVatDocumentsNote', { count: c.documents }),
        debit: signed > 0 ? signed : 0,
        credit: signed < 0 ? -signed : 0,
      };
    });
    if (Math.abs(setup.net) >= 0.005) {
      rows.push({
        key: 'payable',
        account: payableLabel,
        note: setup.net > 0 ? t('netVatPayable') : t('netVatRefundable'),
        debit: setup.net < 0 ? -setup.net : 0,
        credit: setup.net > 0 ? setup.net : 0,
      });
    }
    return rows;
  }, [setup, payableLabel, t]);

  const submit = async () => {
    if (!setup || !returnId) return;
    const v = await form.validateFields();
    setPosting(true);
    try {
      const row = await vatReturnService.postVatReturn(returnId, {
        fta_supplier_id: payable ? v.fta_supplier_id : undefined,
        vat_payable_account_id: v.account_mode === 'existing' ? v.vat_payable_account_id : undefined,
        create_payable_account:
          v.account_mode === 'create'
            ? { account_code: v.new_account_code!.trim(), account_name: v.new_account_name!.trim(), parent_id: setup.suggested_account.parent_id }
            : undefined,
        department_id: v.department_id,
        remember: v.remember,
      });
      message.success(payable ? t('vatReturnPostedWithFtaBill') : t('vatReturnPosted'));
      onPosted(row);
    } catch (e: any) {
      message.error(e?.response?.data?.message || t('errPostFailed'));
    } finally {
      setPosting(false);
    }
  };

  return (
    <Modal
      title={setup ? t('postVatReturnWithNumber', { number: setup.return_number }) : t('postVatReturnModalTitle')}
      open={!!returnId}
      onCancel={onClose}
      onOk={submit}
      cancelText={tc('cancel')}
      okText={payable ? t('postAndRaiseFtaBill') : tc('post')}
      okButtonProps={{ disabled: !setup || blocked, loading: posting }}
      width={760}
      destroyOnClose
    >
      <Spin spinning={loading}>
        {setup && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {setup.stale && (
              <Alert type="error" showIcon message={t('staleVatReturnAlert')} />
            )}
            {setup.unresolved.length > 0 && (
              <Alert
                type="error"
                showIcon
                message={t('unresolvedVatAccountsAlert')}
                description={setup.unresolved.join(', ')}
              />
            )}
            <Descriptions size="small" column={3} bordered>
              <Descriptions.Item label={t('outputVat')}>{money(setup.output_total)}</Descriptions.Item>
              <Descriptions.Item label={t('inputVat')}>{money(setup.input_total)}</Descriptions.Item>
              <Descriptions.Item
                label={
                  setup.net >= 0
                    ? t('netPayableCurrency', { currency: setup.base_currency })
                    : t('netRefundableCurrency', { currency: setup.base_currency })
                }
              >
                <Text strong>{money(Math.abs(setup.net))}</Text>
              </Descriptions.Item>
            </Descriptions>

            <Form form={form} layout="vertical" requiredMark="optional">
              <Form.Item label={t('vatPayableAccount')} name="account_mode" style={{ marginBottom: 8 }}>
                <Radio.Group>
                  <Radio value="existing">{t('useExistingAccount')}</Radio>
                  <Radio value="create">{t('createNewAccount')}</Radio>
                </Radio.Group>
              </Form.Item>
              {mode === 'existing' ? (
                <Form.Item name="vat_payable_account_id" rules={[{ required: true, message: t('selectVatPayableAccount') }]}>
                  <Select showSearch optionFilterProp="label" placeholder={t('liabilityAccountForFtaPlaceholder')} options={accountOptions} />
                </Form.Item>
              ) : (
                <Space align="start" wrap>
                  <Form.Item name="new_account_code" label={t('code')} rules={[{ required: true, message: t('codeRequired') }]}>
                    <Input style={{ width: 140 }} />
                  </Form.Item>
                  <Form.Item name="new_account_name" label={t('name')} rules={[{ required: true, message: t('nameRequired') }]}>
                    <Input style={{ width: 260 }} />
                  </Form.Item>
                  <Form.Item label={t('under')}>
                    <Text type="secondary">{setup.suggested_account.parent_label ?? t('topLevelLiability')}</Text>
                  </Form.Item>
                </Space>
              )}

              {payable && (
                <Space align="start" wrap style={{ width: '100%' }}>
                  <Form.Item
                    name="fta_supplier_id"
                    label={t('taxAuthoritySupplierFta')}
                    rules={[{ required: true, message: t('selectFtaSupplier') }]}
                    style={{ minWidth: 340 }}
                  >
                    <Select showSearch optionFilterProp="label" placeholder={t('supplierVatPaidToPlaceholder')} options={supplierOptions} />
                  </Form.Item>
                  <Form.Item
                    name="department_id"
                    label={t('department')}
                    rules={[{ required: true, message: t('selectDepartmentRequired') }]}
                    style={{ minWidth: 260 }}
                  >
                    <DepartmentSelect />
                  </Form.Item>
                </Space>
              )}
              <Form.Item name="remember" valuePropName="checked" style={{ marginBottom: 0 }}>
                <Checkbox>{t('rememberSettlementSetup')}</Checkbox>
              </Form.Item>
            </Form>

            <div>
              <Text strong>{t('settlementJournalPeriodEnd')}</Text>
              <Table
                size="small"
                pagination={false}
                dataSource={journalRows}
                columns={[
                  { title: t('account'), dataIndex: 'account' },
                  { title: '', dataIndex: 'note', render: (noteText: string) => <Text type="secondary">{noteText}</Text> },
                  { title: t('debit'), dataIndex: 'debit', align: 'right', render: (n: number) => (n ? money(n) : '') },
                  { title: t('credit'), dataIndex: 'credit', align: 'right', render: (n: number) => (n ? money(n) : '') },
                ]}
              />
            </div>
            {payable ? (
              <Alert
                type="info"
                showIcon
                message={t('ftaBillWillBeRaised', {
                  amount: money(setup.net),
                  currency: setup.base_currency,
                  invoiceDate: dayjs(setup.bill_dates.invoice_date).format('DD-MM-YYYY'),
                  dueDate: dayjs(setup.bill_dates.due_date).format('DD-MM-YYYY'),
                })}
              />
            ) : (
              <Alert type="info" showIcon message={t('noVatPayableNoFtaBill')} />
            )}
          </Space>
        )}
      </Spin>
    </Modal>
  );
};

export default PostVatReturnModal;
